import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { User, CreditCard, Sliders, Activity, Key, Calendar, Webhook, Plug, Shield, Trash2 } from 'lucide-react';
import SettingsTabEnhanced from './SettingsTabEnhanced';

const SettingsDialog = ({ open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('settings-profile');
  
  // Check if user is a team member
  const isTeamMember = useMemo(() => {
    return localStorage.getItem('userType') === 'teamMember';
  }, [open]); // Re-check when dialog opens

  // Full tabs for admins
  const adminTabs = [
    { id: 'settings-profile', label: 'Profile', icon: User },
    { id: 'settings-calendar', label: 'Calendar', icon: Calendar },
    { id: 'settings-integrations', label: 'Integrations', icon: Plug },
    { id: 'settings-2fa', label: '2FA Security', icon: Shield },
    { id: 'settings-webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'settings-api-keys', label: 'API Keys', icon: Key },
    { id: 'settings-activity', label: 'Activity Log', icon: Activity },
    { id: 'settings-danger', label: 'Danger Zone', icon: Trash2, danger: true },
  ];

  // Limited tabs for team members - only their profile
  const teamMemberTabs = [
    { id: 'settings-profile', label: 'My Profile', icon: User },
  ];

  const tabs = isTeamMember ? teamMemberTabs : adminTabs;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] overflow-hidden flex flex-col p-0 rounded-xl">
        {/* Mobile: Horizontal Tabs (< 768px) */}
        <div className="md:hidden border-b bg-gray-50 overflow-x-auto flex-shrink-0">
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-lg font-bold text-gray-900">Settings</h2>
          </div>
          <div className="flex gap-1 p-2 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? tab.danger 
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'bg-blue-600 text-white shadow-sm'
                    : tab.danger
                      ? 'text-red-600 bg-red-50 border border-red-200'
                      : 'text-gray-700 bg-white border'
                }`}
              >
                <tab.icon className="w-3 h-3 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Desktop: Sidebar + Content (>= 768px) */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Sidebar - Hidden on Mobile */}
          <div className="hidden md:block w-48 border-r bg-gray-50 overflow-y-auto flex-shrink-0 rounded-tl-2xl">
            <div className="p-4 pb-2">
              <h2 className="text-lg font-bold text-gray-900">Settings</h2>
            </div>
            <div className="px-3 pb-3">
              <div className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === tab.id
                        ? tab.danger
                          ? 'bg-red-600 text-white shadow-sm'
                          : 'bg-blue-600 text-white shadow-sm'
                        : tab.danger
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content Area - Full Width on Mobile */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0 rounded-tr-2xl pt-12 md:pt-16">
            <SettingsTabEnhanced activeSubView={activeTab} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;